import { z } from "zod";

// ─── Room Status ──────────────────────────────────────────────────────────────

export const RoomStatusSchema = z.enum([
  "available",
  "occupied",
  "maintenance",
  "blocked",
]);
export type RoomStatus = z.infer<typeof RoomStatusSchema>;

// ─── Meal Plans ───────────────────────────────────────────────────────────────

export const MealPlanSchema = z.enum(["EP", "CP", "MAP", "AP"]);
export type MealPlan = z.infer<typeof MealPlanSchema>;

export const MEAL_PLANS: {
  key: MealPlan;
  label: string;
  description: string;
}[] = [
  { key: "EP", label: "EP – Room Only", description: "Room Only" },
  {
    key: "CP",
    label: "CP – Room with Breakfast",
    description: "Room with Breakfast",
  },
  {
    key: "MAP",
    label: "MAP – Room with Breakfast & Dinner",
    description: "Room with Breakfast & Dinner",
  },
  {
    key: "AP",
    label: "AP – All Meals Included",
    description: "All Meals Included (Breakfast, Lunch & Dinner)",
  },
];

// ─── Meal Plan Pricing ────────────────────────────────────────────────────────

export const MealPlanPricingSchema = z.object({
  EP: z.number().min(0),
  CP: z.number().min(0),
  MAP: z.number().min(0),
  AP: z.number().min(0),
});
export type MealPlanPricing = z.infer<typeof MealPlanPricingSchema>;

// ─── Room Blocks ──────────────────────────────────────────────────────────────

export const RoomBlockSchema = z.enum(["A", "B"]);
export type RoomBlock = z.infer<typeof RoomBlockSchema>;

// ─── Room Amenity keys ────────────────────────────────────────────────────────

export const ROOM_AMENITIES = [
  { key: "wifi", label: "Wi-Fi" },
  { key: "ac", label: "Air Conditioning" },
  { key: "tv", label: "Smart TV" },
  { key: "minibar", label: "Minibar" },
  { key: "safe", label: "In-room Safe" },
  { key: "hairdryer", label: "Hair Dryer" },
  { key: "bathtub", label: "Bathtub" },
  { key: "kitchenette", label: "Kitchenette" },
  { key: "diningArea", label: "Dining Area" },
  { key: "balcony", label: "Balcony" },
  { key: "lakeView", label: "Lake View" },
] as const;

export type RoomAmenityKey = (typeof ROOM_AMENITIES)[number]["key"];

// ─── Room Type ────────────────────────────────────────────────────────────────

export const RoomTypeSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Name is required"),
  slug: z.string().min(1, "Slug is required"),
  description: z.string().min(1, "Description is required"),
  shortDescription: z.string().min(1, "Short description is required"),
  size: z.number().min(1, "Size must be positive"),
  maxGuests: z.number().min(1, "Must allow at least 1 guest"),
  bedConfig: z.string().min(1, "Bed configuration is required"),
  bathrooms: z.number().min(1),
  // basePrice is the EP price — kept for backward compat
  basePrice: z.number().min(1, "Base price must be positive"),
  mealPlanPricing: MealPlanPricingSchema.optional(),
  // GST is now calculated dynamically from price, but stored for reference
  gstRate: z.number().min(0).max(100).default(12),
  images: z.array(z.string()).default([]),
  amenities: z.array(z.string()).default([]),
  active: z.boolean().default(true),
  sortOrder: z.number().default(0),
  createdAt: z.any().optional(),
  updatedAt: z.any().optional(),
});
export type RoomType = z.infer<typeof RoomTypeSchema>;

export const RoomTypeFormSchema = RoomTypeSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type RoomTypeFormData = z.infer<typeof RoomTypeFormSchema>;

// ─── Individual Room ──────────────────────────────────────────────────────────

export const RoomSchema = z.object({
  id: z.string(),
  number: z.string().min(1, "Room number is required"),
  floor: z.number().min(0, "Floor is required"),
  block: RoomBlockSchema.optional(),
  roomTypeId: z.string().min(1, "Room type is required"),
  status: RoomStatusSchema.default("available"),
  notes: z.string().default(""),
  createdAt: z.any().optional(),
  updatedAt: z.any().optional(),
});
export type Room = z.infer<typeof RoomSchema>;

export const RoomFormSchema = RoomSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type RoomFormData = z.infer<typeof RoomFormSchema>;

// ─── Room with its type attached (used in UI) ─────────────────────────────────

export type RoomWithType = Room & {
  roomType: RoomType | null;
};

// ─── Status display helpers ───────────────────────────────────────────────────

export const ROOM_STATUS_CONFIG: Record<
  RoomStatus,
  { label: string; color: string; bg: string }
> = {
  available: {
    label: "Available",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  occupied: { label: "Occupied", color: "text-blue-700", bg: "bg-blue-50" },
  maintenance: {
    label: "Maintenance",
    color: "text-amber-700",
    bg: "bg-amber-50",
  },
  blocked: { label: "Blocked", color: "text-red-700", bg: "bg-red-50" },
};